// Cryptic session builder + answer checking.
//
// Difficulty here is the mean LLM solve-rate (0..1) baked into each puzzle — LOW rate =
// HARD (the models struggled). This doubles as the human-vs-LLM comparison anchor: the
// per-band human solve-rate correlation against these LLM rates is the headline analysis,
// so we deliberately spread each test session across the difficulty range (one clue per
// band) exactly as the 24 test spans Wurgaft's deciles.

import { CrypticPuzzle } from './cryptic.data'
import { matchSpokenAnswer } from '../../../shared/labs/answerMatch'

export type CrypticDifficulty = 'easy' | 'medium' | 'hard'

// Coarse tier from LLM solve-rate, for free-play selection + display.
export const tierOf = (llmSolveRate: number): CrypticDifficulty =>
  llmSolveRate >= 0.66 ? 'easy' : llmSolveRate >= 0.33 ? 'medium' : 'hard'

// A session puzzle keeps its difficulty BAND (0 hardest … n-1 easiest) so the saved trace
// records which band it was drawn from, mirroring the 24 game's `decile`. `conditionId` is set
// when the test came from a pre-determined group (the 24-game model); null for band-draw / free play.
export type CrypticSessionPuzzle = CrypticPuzzle & {
  difficulty: CrypticDifficulty
  band: number | null
  conditionId: number | null
}

const decorate = (p: CrypticPuzzle, band: number | null, conditionId: number | null = null): CrypticSessionPuzzle => ({
  ...p,
  difficulty: tierOf(p.llmSolveRate),
  band,
  conditionId,
})

const shuffle = <T>(arr: readonly T[]): T[] => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// TEST mode: `n` clues spread across the difficulty range — sort by LLM solve-rate, split
// into `n` contiguous bands, pick one random clue from each (band 0 = hardest), then shuffle
// presentation order. One clue per band gives per-difficulty coverage for the correlation.
// `pool` is the variant's clue set (standard hard-100 or the easier Quick-Cryptic pool).
export const buildCrypticTest = (pool: CrypticPuzzle[], n = 5): CrypticSessionPuzzle[] => {
  const byHardest = [...pool].sort((a, b) => a.llmSolveRate - b.llmSolveRate)
  const size = Math.floor(byHardest.length / n)
  const picked: CrypticSessionPuzzle[] = []
  for (let band = 0; band < n; band++) {
    const start = band * size
    const slice = band === n - 1 ? byHardest.slice(start) : byHardest.slice(start, start + size)
    const p = slice[Math.floor(Math.random() * slice.length)]
    picked.push(decorate(p, band))
  }
  return shuffle(picked)
}

// TEST mode (24-game model): pre-determined groups of 10 allocated at random. `conditions` is a
// list of id-groups in band/decile order (0 hardest … 9 easiest); a session gets ONE whole group,
// each clue tagged with its band + the conditionId, then shuffled for presentation. Used by the
// easier cryptic arm; the standard arm still uses buildCrypticTest's per-band random draw.
export const buildCrypticTestFromConditions = (pool: CrypticPuzzle[], conditions: string[][]): CrypticSessionPuzzle[] => {
  const byId = new Map(pool.map((p) => [p.id, p]))
  const conditionId = Math.floor(Math.random() * conditions.length)
  const picked = conditions[conditionId]
    .map((id, band) => {
      const p = byId.get(id)
      return p ? decorate(p, band, conditionId) : null
    })
    .filter((x): x is CrypticSessionPuzzle => x !== null)
  return shuffle(picked)
}

// WARM-UP: one random reserved practice clue (unscored, never in the test/free-play pool).
// The player sees the warm-up at most once, so a single random pick keeps it low-stakes.
export const pickCrypticPractice = (practicePool: CrypticPuzzle[]): CrypticSessionPuzzle =>
  decorate(practicePool[Math.floor(Math.random() * practicePool.length)], null)

// FREE PLAY: one random clue, optionally filtered to a coarse tier. No band (not a test).
export const pickCrypticPlay = (pool: CrypticPuzzle[], difficulty?: CrypticDifficulty): CrypticSessionPuzzle => {
  const filtered = difficulty ? pool.filter((p) => tierOf(p.llmSolveRate) === difficulty) : pool
  const src = filtered.length ? filtered : pool
  return decorate(src[Math.floor(Math.random() * src.length)], null)
}

// ---- answer checking ----

// Normalise for comparison: uppercase, strip everything but A–Z and 0–9 (so spacing,
// punctuation and the enumeration's word breaks don't matter — "HIGH CARD" == "highcard").
export const normalizeAnswer = (s: string): string => s.toUpperCase().replace(/[^A-Z0-9]/g, '')

export type CrypticVerdict = {
  correct: boolean
  matched: string | null // which accepted answer it matched (canonical form), if any
}

// Every accepted form, with pipe-alternatives split out and any hand-curated spoken/homophone
// variants folded in.
export const acceptedForms = (puzzle: CrypticPuzzle): string[] =>
  [puzzle.answer, ...puzzle.alternatives, ...(puzzle.spokenVariants || [])]
    .flatMap((s) => s.split('|'))
    .map((s) => s.trim())
    .filter(Boolean)

export const checkCrypticAnswer = (input: string, puzzle: CrypticPuzzle): CrypticVerdict => {
  const guess = normalizeAnswer(input)
  if (!guess) return { correct: false, matched: null }
  const forms = acceptedForms(puzzle)
  const exact = forms.find((a) => normalizeAnswer(a) === guess)
  if (exact) return { correct: true, matched: exact }
  // tolerate the answer phrased inside a sentence (typed or spoken)
  const spoken = matchSpokenAnswer(input, forms)
  return { correct: !!spoken, matched: spoken }
}

// Detect the spoken answer in the committed transcript, to pre-fill the answer box after commit.
export const detectCrypticAnswer = (transcript: string, puzzle: CrypticPuzzle): string | null =>
  matchSpokenAnswer(transcript, acceptedForms(puzzle))
